import React, { PropsWithChildren } from 'react';
import { Trans, t } from '@lingui/macro';
import { Group, Text, Box, Radio, LightMode } from '@landr/maestro';
import { Interval, CurrencyDto } from 'types/generated/subscriptionsApi';
import { addCurrencyToPrice } from 'utils/price';
import InfoPopover from 'components/InfoPopover/InfoPopover';
import { HandlePlanSelect } from '../../types';
import * as Styled from './styled';

export type PlanSelectorProps = {
    frequency: Interval;
    activePlanCode?: string;
    creditsNeeded?: number;
    handlePlanSelect: HandlePlanSelect;
};

const Usp: React.FC<PropsWithChildren> = ({ children }) => (
    <Box textAlign="left">
        <Text textStyle="body-sm">{children}</Text>
    </Box>
);

const MonthlyPrice = ({
    monthlyEquivalentPrice,
    monthlyEquivalentSale,
    isVerticalLayout,
}: {
    monthlyEquivalentPrice: string;
    monthlyEquivalentSale?: string;
    isVerticalLayout?: boolean;
}) => {
    const paidPrice = monthlyEquivalentSale || monthlyEquivalentPrice;

    return (
        <Box textAlign={isVerticalLayout ? 'left' : 'right'}>
            <Trans>
                <Text textStyle="lead" fontWeight="bold">
                    {paidPrice}
                </Text>
                <Text textStyle="body-sm"> /mo</Text>
            </Trans>
        </Box>
    );
};

const BilledUpfront = ({
    subscriptionPrice,
    salePrice,
    isMonthly,
}: {
    subscriptionPrice: string;
    salePrice?: string;
    isMonthly: boolean;
}) => {
    if (isMonthly) {
        return <>&nbsp;</>;
    }
    // To preserve layout shift, render a placeholder component

    const paidPrice = salePrice || subscriptionPrice;

    return (
        <Text color="text.medium" textStyle="body-sm">
            {!!salePrice && (
                <Box
                    as="span"
                    textDecoration="line-through"
                    color="danger.500"
                    paddingRight="2xs"
                >
                    {subscriptionPrice}
                </Box>
            )}
            <Trans>{paidPrice} billed upfront</Trans>
        </Text>
    );
};

const MostPopular = ({ isMostPopular }: { isMostPopular: boolean }) => {
    if (!isMostPopular) {
        return null;
    }

    return (
        <Box className={Styled.mostPopularWrapper}>
            <Text textStyle="caption">
                <Trans>Most popular</Trans>
            </Text>
        </Box>
    );
};

const PlanHeader = ({
    isChecked,
    title,
    info,
    monthlyEquivalentPrice,
    monthlyEquivalentSale,
    isVerticalLayout,
}: {
    isChecked: boolean;
    title: string;
    info?: string;
    monthlyEquivalentPrice: string;
    monthlyEquivalentSale: string;
    isVerticalLayout?: boolean;
}) => (
    <Group
        gap="none"
        direction={isVerticalLayout ? 'vertical' : 'horizontal'}
        width="100%"
    >
        <Group.Item flexDirection="row" width="100%">
            <Group gap="xs">
                <Group.Item>
                    <Group gap="xs">
                        <Group.Item>
                            <Text
                                display="inline-block"
                                width="100%"
                                fontWeight="bold"
                                textAlign="left"
                            >
                                {title}
                            </Text>
                        </Group.Item>
                        {info && (
                            <Group.Item>
                                <InfoPopover
                                    aria-label={t`Details Tooltip`}
                                    isInverted={false}
                                    isOutlined={false}
                                    size="sm"
                                >
                                    {info}
                                </InfoPopover>
                            </Group.Item>
                        )}
                    </Group>
                </Group.Item>
            </Group>
        </Group.Item>
        <Group.Item
            minWidth="112px"
            alignItems="flex-start"
            justifyContent="flex-start"
        >
            <MonthlyPrice
                monthlyEquivalentPrice={monthlyEquivalentPrice}
                monthlyEquivalentSale={monthlyEquivalentSale}
                isVerticalLayout={isVerticalLayout}
            />
        </Group.Item>
    </Group>
);

const PlanContent = ({
    isMonthly,
    subscriptionPrice,
    salePrice,
    usp,
}: {
    isMonthly: boolean;
    subscriptionPrice: string;
    salePrice: string;
    usp: JSX.Element;
}) => (
    <Group gap="xs" verticalAlign="middle" width="100%">
        <Group.Item textAlign="left">
            <Group direction="vertical" gap="xs">
                <Group.Item>
                    <BilledUpfront
                        subscriptionPrice={subscriptionPrice}
                        salePrice={salePrice}
                        isMonthly={isMonthly}
                    />
                </Group.Item>
                <Group.Item>
                    <Usp>{usp}</Usp>
                </Group.Item>
            </Group>
        </Group.Item>
    </Group>
);

const Plan = ({
    isChecked,
    code,
    currency,
    frequency,
    isMostPopular = false,
    monthlyEquivalentPrice,
    subscriptionPrice,
    title,
    info,
    usp,
    handlePlanSelect,
    salePrice,
    monthlyEquivalentSale,
    isVerticalLayout,
}: {
    isChecked: boolean;
    code?: string;
    currency?: CurrencyDto;
    frequency: Interval;
    isMostPopular?: boolean;
    monthlyEquivalentPrice?: number;
    subscriptionPrice?: number;
    title: string;
    info?: string;
    usp: JSX.Element;
    handlePlanSelect: HandlePlanSelect;
    salePrice?: number;
    monthlyEquivalentSale?: number;
    isVerticalLayout?: boolean;
}) => {
    if (!code || !currency || !subscriptionPrice || !monthlyEquivalentPrice) {
        return null;
    }

    const monthlyEquivalentPriceWithCurrency = addCurrencyToPrice(
        currency,
        monthlyEquivalentPrice,
    );
    const subscriptionPriceWithCurrency = addCurrencyToPrice(
        currency,
        subscriptionPrice,
    );

    const monthlyEquivalentSaleWithCurrency = addCurrencyToPrice(
        currency,
        monthlyEquivalentSale,
    );

    const salePriceWithCurrency = addCurrencyToPrice(currency, salePrice);

    return (
        <Group.Item>
            <Box
                className={Styled.planWrapper}
                onClick={() => handlePlanSelect(code)}
            >
                <MostPopular isMostPopular={isMostPopular} />
                <Box className={Styled.innerWrapper}>
                    <Group gap="xs">
                        <Group.Item width="xs">
                            <LightMode>
                                <Radio
                                    onChange={() => undefined}
                                    isChecked={isChecked}
                                    label=""
                                    padding="none"
                                    pointerEvents="none"
                                />
                            </LightMode>
                        </Group.Item>
                        <Group.Item width="100%">
                            <Group gap="none" direction="vertical">
                                <Group.Item>
                                    <PlanHeader
                                        isChecked={isChecked}
                                        title={title}
                                        info={info}
                                        monthlyEquivalentPrice={
                                            monthlyEquivalentPriceWithCurrency
                                        }
                                        monthlyEquivalentSale={
                                            monthlyEquivalentSaleWithCurrency
                                        }
                                        isVerticalLayout={isVerticalLayout}
                                    />
                                </Group.Item>
                                <Group.Item>
                                    <PlanContent
                                        isMonthly={frequency === 'Monthly'}
                                        subscriptionPrice={
                                            subscriptionPriceWithCurrency
                                        }
                                        salePrice={salePriceWithCurrency}
                                        usp={usp}
                                    />
                                </Group.Item>
                            </Group>
                        </Group.Item>
                    </Group>
                </Box>
            </Box>
        </Group.Item>
    );
};

export default Plan;
